import { GraphQLClient } from "graphql-request";
import { SUBSCRIPTION_PRICES } from "./queries";
import {
    BackendSubscription,
    SubscriptionPricesResponse,
    PricingTier,
    PricingFeature,
} from "./types";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const formatPrice = (priceCents: number, currency: string): string => {
    const price = priceCents / 100;
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(price);
};

export const calculateYearlyDiscountPercent = (
    tier: PricingTier,
): number | null => {
    const monthlyPrice = tier.prices.find(
        (price) => price.type === "monthly",
    );
    const yearlyPrice = tier.prices.find(
        (price) => price.type === "yearly",
    );

    if (
        !monthlyPrice ||
        !yearlyPrice ||
        monthlyPrice.priceCents <= 0 ||
        monthlyPrice.currency !== yearlyPrice.currency
    ) {
        return null;
    }

    const regularYearlyPrice = monthlyPrice.priceCents * 12;
    const savings = regularYearlyPrice - yearlyPrice.priceCents;

    if (savings <= 0) {
        return null;
    }

    return Math.round((savings / regularYearlyPrice) * 100);
};

export const getMaximumYearlyDiscountPercent = (
    tiers: PricingTier[],
): number | null => {
    const discounts = tiers
        .map(calculateYearlyDiscountPercent)
        .filter((discount): discount is number => discount !== null);

    if (discounts.length === 0) {
        return null;
    }

    return Math.max(...discounts);
};

const getDisplaySubtext = (type: "month" | "year", t: TFunction): string => {
    return type === "month" ? t("pricing.price.perMonth") : t("pricing.price.perYear");
};

const generateFeatures = (
    subscription: BackendSubscription,
    t: TFunction,
): PricingFeature[] => {
    const features: PricingFeature[] = [];

    if (subscription.maxProjects === -1) {
        features.push({ text: t("pricing.features.projectsUnlimited"), included: true });
    } else if (subscription.maxProjects > 0) {
        features.push({
            text: t("pricing.features.projectsLimit", { count: subscription.maxProjects }),
            included: true,
        });
    }

    if (subscription.maxSongsPerProject === -1) {
        features.push({ text: t("pricing.features.songsUnlimited"), included: true });
    } else {
        features.push({
            text: t("pricing.features.songsLimit", { count: subscription.maxSongsPerProject }),
            included: true,
        });
    }

    if (subscription.maxSetListsPerProject === -1) {
        features.push({ text: t("pricing.features.setlistsUnlimited"), included: true });
    } else if (subscription.maxSetListsPerProject > 0) {
        features.push({
            text: t("pricing.features.setlistsLimit", { count: subscription.maxSetListsPerProject }),
            included: true,
        });
    }

    if (subscription.maxGigsPerProject === -1) {
        features.push({ text: t("pricing.features.gigsUnlimited"), included: true });
    } else if (subscription.maxGigsPerProject > 0) {
        features.push({
            text: t("pricing.features.gigsLimit", { count: subscription.maxGigsPerProject }),
            included: true,
        });
    }

    if (subscription.maxStorageMB > 0) {
        const storageGB = subscription.maxStorageMB / 1024;
        features.push({
            text: t("pricing.features.storage", { size: storageGB }),
            included: true,
        });
    } else {
        features.push({ text: t("pricing.features.storageNone"), included: false });
    }

    features.push({
        text: t("pricing.features.analytics"),
        included: subscription.supportsAnalytics,
    });

    features.push({
        text: t("pricing.features.collaboration"),
        included: subscription.supportsCollaboration,
    });

    features.push({
        text: t("pricing.features.prioritySupport"),
        included: subscription.supportsPrioritySupport,
    });

    return features;
};

export const transformToPricingTiers = (
    subscriptions: BackendSubscription[],
    t: TFunction,
): PricingTier[] => {
    if (!subscriptions || !Array.isArray(subscriptions)) {
        return [];
    }

    const tiers: PricingTier[] = [];

    const availableSubscriptions = subscriptions.filter(
        (subscription) => subscription.isAvailable && !subscription.isDeleted
    );

    availableSubscriptions.forEach((subscription) => {
        const availablePrices = subscription.subscriptionPlanPrices.filter(
            (price) => price.isAvailable && !price.isDeleted,
        );

        const hasMonthly = availablePrices.some((p) => p.type === "month");
        const hasYearly = availablePrices.some((p) => p.type === "year");

        if (!hasMonthly || !hasYearly) {
            return;
        }

        const tier: PricingTier = {
            id: subscription.id,
            name: subscription.name,
            description: subscription.description,
            prices: availablePrices.map((price) => ({
                type: price.type === "month" ? "monthly" : "yearly",
                priceCents: price.priceCents,
                currency: price.currency,
                externalPriceId: price.externalPriceId,
                displayPrice: formatPrice(price.priceCents, price.currency),
                displaySubtext: getDisplaySubtext(price.type, t),
            })),
            features: generateFeatures(subscription, t),
            highlighted: subscription.displayOrder === 2,
        };

        tiers.push(tier);
    });

    return tiers.sort((a, b) => {
        const aSubscription = subscriptions.find((s) => s.id === a.id);
        const bSubscription = subscriptions.find((s) => s.id === b.id);
        return (
            (aSubscription?.displayOrder || 0) - (bSubscription?.displayOrder || 0)
        );
    });
};

export const fetchSubscriptionPrices = async (
    graphqlClient: GraphQLClient,
    t: TFunction,
): Promise<PricingTier[]> => {
    try {
        const data =
            await graphqlClient.request<SubscriptionPricesResponse>(
                SUBSCRIPTION_PRICES,
            );

        return transformToPricingTiers(data.subscriptions, t);
    } catch (error) {
        throw new Error(t("pricing.error.loadDetails"));
    }
};
